import { AxiosResponse } from 'axios';
import axios, { Canceler } from 'axios';
import { TApi } from 'libs/api';
import { makeSearchParamsObject } from 'libs/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import usePagination, {
  initialMeta,
  IPaginationMeta,
  IResponseMeta,
  LoadResourcesParams,
} from './usePagination';
import useHandleHttpError from './useHandleHttpError';

interface IState<T> {
  resources: T[];
  isLoading: boolean;
}

interface IFetchResources<T> {
  setter?: (resources: T[]) => (state: { resources: T[] }) => { resources: T[] };
}

interface IUsePaginatedResources<T> {
  resources: T[];
  loadMore: () => void;
  fetchData: () => void;
  hasMore: boolean;
  paginationMeta: IPaginationMeta;
  isLoading: boolean;
}

interface IUsePaginatedResourcesProps {
  api: TApi;
  search: URLSearchParams;
  locationSearch: string;
  initialLoad?: boolean;
  onLoadedMore?: (meta: IPaginationMeta) => void;
  initialMetaValues?: IPaginationMeta;
  params?: object;
}

export default function usePaginatedResources<T>({
  api,
  search,
  locationSearch,
  onLoadedMore,
  initialLoad = true,
  initialMetaValues = initialMeta,
  params = {},
}: IUsePaginatedResourcesProps): IUsePaginatedResources<T> {
  const handleHttpError = useHandleHttpError();
  const cancel = useRef<Canceler | null>(null);
  const [{ resources, isLoading }, setResources] = useState<IState<T>>({
    resources: [],
    isLoading: false,
  });
  const loadResources = useCallback(
    async ({
      params = {},
      onSuccess,
      meta,
      setter = (responseData) => (state) => ({ resources: [...state.resources, ...responseData] }),
    }: LoadResourcesParams & IFetchResources<T>) => {
      try {
        setResources((state) => ({ ...state, isLoading: true }));
        const response: AxiosResponse<{
          resources: T[];
          meta: IResponseMeta;
        }> = await api({
          // Can't import CancelToken as
          // import { CancelToken } from 'axios';
          // Because in this case CancelToken is interface but need class.
          cancelToken: new axios.CancelToken((cancelHandler) => {
            cancel.current = cancelHandler;
          }),
          params: {
            ...params,
            ...meta,
          },
        });
        setResources((state) => ({ ...setter(response.data.resources)(state), isLoading: false }));
        onSuccess && onSuccess(response.data.meta);
      } catch (error) {
        // Can't import Cancel as
        // import { Cancel } from 'axios';
        // Because in this case Cancel is interface but need class.
        if (!(error instanceof axios.Cancel)) {
          setResources((state) => ({ ...state, isLoading: false }));
        }
        handleHttpError(error);
      }
    },
    []
  );

  const {
    loadMore,
    hasMore,
    onSuccess: onSuccessLoaded,
    meta: paginationMeta,
  } = usePagination({
    loadResources,
    initialMetaValues,
    onLoadedMore,
  });

  const fetchData = useCallback(() => {
    loadResources({
      meta: { ...initialMeta, per_page: paginationMeta.page * paginationMeta.per_page },
      onSuccess: onSuccessLoaded,
      params: { ...params, ...makeSearchParamsObject(search) },
      setter: (data) => () => ({ resources: data }),
    });
  }, [
    params,
    makeSearchParamsObject,
    initialMeta,
    paginationMeta.page,
    paginationMeta.per_page,
    onSuccessLoaded,
    loadResources,
  ]);

  useEffect(() => {
    initialLoad && fetchData();
  }, [locationSearch, initialLoad]);

  useEffect(() => {
    return () => {
      if (cancel.current !== null) {
        cancel.current();
      }
    };
  }, [cancel]);

  return {
    fetchData,
    hasMore,
    loadMore: () => loadMore({ ...params, ...makeSearchParamsObject(search) }),
    resources,
    isLoading,
    paginationMeta,
  };
}
